import { useEffect } from "react";
import {
  CityNamesIcon,
  FacilityNamesIcon,
  FacilityOwnerIcon,
} from "../../icons";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useNitroStore } from "../../store/Store";

type SupportedLabelType = "city" | "location" | "facility";

type LabelOption = {
  label: string;
  value: SupportedLabelType;
  Icon: ({ color }: { color?: string }) => JSX.Element;
};

const labelOptions: LabelOption[] = [
  {
    label: "Facility Names",
    value: "location",
    Icon: FacilityNamesIcon,
  },
  {
    label: "City Names",
    value: "city",
    Icon: CityNamesIcon,
  },
  {
    label: "Facility Owner",
    value: "facility",
    Icon: FacilityOwnerIcon,
  },
];

const isSupportedLabelType = (
  labelType?: string,
): labelType is SupportedLabelType => {
  return (
    labelType === "city" || labelType === "location" || labelType === "facility"
  );
};

const LabelTypeButtons = () => {
  const isMobile = useIsMobile(768);
  const [labelType, setLabelType] = useNitroStore((state) => [
    state.labelType,
    state.setLabelType,
  ]);

  const selectedLabelType = isSupportedLabelType(labelType)
    ? labelType
    : "location";

  useEffect(() => {
    if (isSupportedLabelType(labelType)) return;

    setLabelType("location");
  }, [labelType, setLabelType]);

  if (isMobile) return null;

  return (
    <section className="labelTypeButtons" aria-label="Location label controls">
      {labelOptions.map(({ label, value, Icon }) => {
        const isSelected = selectedLabelType === value;
        const iconColor = isSelected ? "#ffffff" : "#475569";

        return (
          <button
            aria-pressed={isSelected}
            className={`labelTypeButton ${
              isSelected ? "labelTypeButtonActive" : "labelTypeButtonInactive"
            }`}
            key={value}
            onClick={() => {
              if (isSelected) return;

              setLabelType(value);
            }}
            type="button"
          >
            <span aria-hidden="true" className="labelTypeButtonIcon">
              <Icon color={iconColor} />
            </span>
            <span className="labelTypeButtonLabel">{label}</span>
          </button>
        );
      })}
    </section>
  );
};

export default LabelTypeButtons;
